import styled from '@emotion/styled';
import Container from '@ep/components/Container';
import theme from '@ep/theme';
import { Gcms_Testimonial } from '@ep/types/gatsbyTypes';
import useScroll from '@ep/utils/useScroll';
import React, { useRef } from 'react';
import Testimonial from './Testimonial';

export interface TestimonialModuleProps {
  label?: string;
  testimonials: Gcms_Testimonial[];
  backgroundColor?: string;
}

const StyledContainer = styled(Container)`
  max-width: 1000px;
  padding: 2rem 0;
  ${theme.mediaQueries.mobile} {
    padding-top: 1.5rem;
  }
  ${theme.mediaQueries.tablet} {
    padding-top: 1.5rem;
  }
`;

interface WrapperProps {
  themeColor?: string;
}

const Wrapper = styled.div<WrapperProps>`
  ${({ themeColor }) =>
    `
    color:${themeColor &&
      themeColor != theme.colors.white &&
      theme.colors.white};
  `}

  ${theme.mediaQueries.tablet} {
    margin: 0 auto;
    max-width: 28rem;
  }
`;

const Testimonials = styled.div`
  display: flex;
  position: relative;
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const PageIndicators = styled.div`
  display: flex;
  justify-content: center;
  clear: both;
`;

interface PageIndicatorProps {
  active: boolean;
  themeColor?: string;
}

const PageIndicator = styled.div<PageIndicatorProps>`
  margin: 1rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: transparent;
  transition: ${theme.transitions.create(['background-color', 'border-color'])};
  cursor: pointer;

  ${({ themeColor }) =>
    `
    border: 1px solid ${
      themeColor && themeColor != theme.colors.white
        ? theme.colors.white
        : theme.colors.mediumBlue
    };
  `}

  ${({ active, themeColor }) =>
    active
      ? `
        background-color: ${
          themeColor && themeColor != theme.colors.white
            ? theme.colors.white
            : theme.colors.mediumBlue
        };
        
      `
      : `
        background-color: ${themeColor}
      `}
`;

function TestimonialModule({
  testimonials,
  backgroundColor,
}: TestimonialModuleProps) {
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const { x } = useScroll(testimonialsRef);
  const currentIndex = testimonialsRef.current
    ? Math.round(x / testimonialsRef.current.clientWidth)
    : 0;

  function scrollTo(index: number) {
    testimonialsRef.current?.scroll({
      left: (testimonialsRef.current.children[index] as HTMLElement).offsetLeft,
      behavior: 'smooth',
    });
  }

  return (
    <StyledContainer backgroundColor={backgroundColor}>
      <Wrapper themeColor={backgroundColor}>
        <Testimonials ref={testimonialsRef} tabIndex={0}>
          {testimonials &&
            testimonials.map(testimonial => (
              <Testimonial
                key={testimonial.id}
                testimonial={testimonial}
                themeColor={backgroundColor}
              />
            ))}
        </Testimonials>
        <PageIndicators>
          {testimonials &&
            testimonials.length > 1 &&
            testimonials.map((testimonial, index) => (
              <PageIndicator
                key={testimonial.id}
                active={currentIndex === index}
                onClick={() => scrollTo(index)}
                themeColor={backgroundColor}
              />
            ))}
        </PageIndicators>
      </Wrapper>
    </StyledContainer>
  );
}

export default React.memo(TestimonialModule);
